import { Locale } from '../models/content.model';

export type UiKey =
	| 'search.dialog-label'
	| 'search.no-results'
	| 'search.placeholder'
	| 'search.results-count'
	| 'search.trigger'
	| 'doc.breadcrumb'
	| 'doc.copied'
	| 'doc.copy-code'
	| 'doc.copy-page'
	| 'doc.on-this-page'
	| `note.${'note' | 'tip' | 'important' | 'warning'}`
	| 'doc.next'
	| 'doc.previous'
	| 'footer.copyright'
	| 'footer.disclaimer'
	| 'footer.privacy'
	| 'footer.terms'
	| `lang.${Locale}`
	| 'lightbox.close'
	| 'lightbox.label'
	| 'lightbox.next'
	| 'lightbox.previous'
	| 'nav.application'
	| 'nav.application.credentials'
	| 'nav.application.credentials.access-kit'
	| 'nav.application.credentials.credentials-list'
	| 'nav.application.credentials.credit-cards'
	| 'nav.application.credentials.levels'
	| 'nav.application.settings'
	| 'nav.application.settings.access-settings'
	| 'nav.application.settings.account-settings'
	| 'nav.application.settings.security-settings'
	| 'nav.application.side-menu'
	| 'nav.authentication'
	| 'nav.authentication.connect-application'
	| 'nav.authentication.connect-console'
	| 'nav.authentication.connect-extension'
	| 'nav.console'
	| 'nav.console.access-kit'
	| 'nav.console.assistant'
	| 'nav.console.credit-cards'
	| 'nav.console.grc'
	| 'nav.console.home'
	| 'nav.console.profile'
	| 'nav.console.systems'
	| 'nav.console.users'
	| 'nav.console.workstation-protect'
	| 'nav.extension'
	| 'nav.workstation-protect'
	| 'nav.faq'
	| 'nav.faq.application'
	| 'nav.faq.console'
	| 'nav.faq.extension'
	| 'nav.faq.workstation-protect'
	| 'nav.troubleshooting'
	| 'nav.troubleshooting.application'
	| 'nav.troubleshooting.console'
	| 'nav.troubleshooting.extension'
	| 'nav.troubleshooting.workstation-protect'
	| 'nav.installation'
	| 'nav.installation.install-application'
	| 'nav.installation.install-extension'
	| 'nav.user-guide'
	| 'nav.video';

export const UI_STRINGS: Record<Locale, Record<UiKey, string>> = {
	en: {
		'search.dialog-label': 'Search documentation',
		'search.no-results': 'No results for “{{query}}”.',
		'search.placeholder': 'Search documentation',
		'search.results-count': '{{count}} results',
		'search.trigger': 'Search...',
		'doc.breadcrumb': 'Breadcrumb',
		'doc.copied': 'Copied',
		'note.note': 'Note:',
		'note.tip': 'Tip:',
		'note.important': 'Important:',
		'note.warning': 'Warning:',
		'doc.copy-code': 'Copy code',
		'doc.copy-page': 'Copy Page',
		'doc.on-this-page': 'On this page',
		'doc.next': 'Next',
		'doc.previous': 'Previous',
		'footer.copyright': 'Copyright {{year}} MyCena Limited. All rights reserved.',
		'footer.disclaimer': 'Disclaimer',
		'footer.privacy': 'Privacy Policy',
		'footer.terms': 'Terms & Conditions',
		'lang.de': 'Deutsch',
		'lang.en': 'English',
		'lang.es': 'Español',
		'lang.fr': 'Français',
		'lang.it': 'Italiano',
		'lang.pt': 'Português',
		'lang.zh-TW': '繁體中文',
		'lightbox.close': 'Close',
		'lightbox.label': 'Screenshot viewer',
		'lightbox.next': 'Next screenshot',
		'lightbox.previous': 'Previous screenshot',
		'nav.application': 'Application',
		'nav.application.credentials': 'Credentials',
		'nav.application.credentials.access-kit': 'Access Kit',
		'nav.application.credentials.credentials-list': 'Credentials list',
		'nav.application.credentials.credit-cards': 'Credit Cards',
		'nav.application.credentials.levels': 'Levels',
		'nav.application.settings': 'Settings',
		'nav.application.settings.access-settings': 'Access to settings',
		'nav.application.settings.account-settings': 'Account settings',
		'nav.application.settings.security-settings': 'Security settings',
		'nav.application.side-menu': 'Side Menu',
		'nav.authentication': 'Authentication',
		'nav.authentication.connect-application': 'How to connect to application',
		'nav.authentication.connect-console': 'How to connect to console',
		'nav.authentication.connect-extension': 'How to connect to extension',
		'nav.console': 'Console',
		'nav.console.access-kit': 'Access Kit',
		'nav.console.assistant': 'AI Assistant',
		'nav.console.credit-cards': 'Credit Cards',
		'nav.console.grc': 'GRC',
		'nav.console.home': 'Home',
		'nav.console.profile': 'Profile',
		'nav.console.systems': 'Systems',
		'nav.console.workstation-protect': 'Workstation Protect',
		'nav.console.users': 'Users',
		'nav.extension': 'Extension',
		'nav.workstation-protect': 'Workstation Protect',
		'nav.faq': 'FAQs',
		'nav.faq.application': 'Application',
		'nav.faq.console': 'Console',
		'nav.faq.extension': 'Extension',
		'nav.faq.workstation-protect': 'Workstation Protect',
		'nav.troubleshooting': 'Troubleshooting',
		'nav.troubleshooting.application': 'Application',
		'nav.troubleshooting.console': 'Console',
		'nav.troubleshooting.extension': 'Extension',
		'nav.troubleshooting.workstation-protect': 'Workstation Protect',
		'nav.installation': 'Installation',
		'nav.installation.install-application': 'Install application',
		'nav.installation.install-extension': 'Install extension',
		'nav.user-guide': 'User Guide',
		'nav.video': 'How to videos',
	},
	pt: {
		'search.dialog-label': 'Pesquisar na documentação',
		'search.no-results': 'Nenhum resultado para “{{query}}”.',
		'search.placeholder': 'Pesquisar na documentação',
		'search.results-count': '{{count}} resultados',
		'search.trigger': 'Pesquisar...',
		'doc.breadcrumb': 'Trilha de navegação',
		'doc.copied': 'Copiado',
		'note.note': 'Nota:',
		'note.tip': 'Dica:',
		'note.important': 'Importante:',
		'note.warning': 'Aviso:',
		'doc.copy-code': 'Copiar código',
		'doc.copy-page': 'Copiar página',
		'doc.on-this-page': 'Nesta página',
		'doc.next': 'Próximo',
		'doc.previous': 'Anterior',
		'footer.copyright': 'Copyright {{year}} MyCena Limited. Todos os direitos reservados.',
		'footer.disclaimer': 'Aviso Legal',
		'footer.privacy': 'Política de Privacidade',
		'footer.terms': 'Termos e Condições',
		'lang.de': 'Deutsch',
		'lang.en': 'English',
		'lang.es': 'Español',
		'lang.fr': 'Français',
		'lang.it': 'Italiano',
		'lang.pt': 'Português',
		'lang.zh-TW': '繁體中文',
		'lightbox.close': 'Fechar',
		'lightbox.label': 'Visualizador de capturas de tela',
		'lightbox.next': 'Próxima captura de tela',
		'lightbox.previous': 'Captura de tela anterior',
		'nav.application': 'Aplicativa',
		'nav.application.credentials': 'Credenciais',
		'nav.application.credentials.access-kit': 'Kit de acesso',
		'nav.application.credentials.credentials-list': 'Lista de credenciais',
		'nav.application.credentials.credit-cards': 'Cartões de crédito',
		'nav.application.credentials.levels': 'Níveis',
		'nav.application.settings': 'Configurações',
		'nav.application.settings.access-settings': 'Acesso às configurações',
		'nav.application.settings.account-settings': 'Configurações de Conta',
		'nav.application.settings.security-settings': 'Configurações de segurança',
		'nav.application.side-menu': 'Menu lateral',
		'nav.authentication': 'Autenticação',
		'nav.authentication.connect-application': 'Como se conectar ao aplicativo',
		'nav.authentication.connect-console': 'Como se conectar ao console',
		'nav.authentication.connect-extension': 'Como se conectar à extensão',
		'nav.console': 'Console',
		'nav.console.access-kit': 'Kit de Acesso',
		'nav.console.assistant': 'Assistente IA',
		'nav.console.credit-cards': 'Cartões de crédito',
		'nav.console.grc': 'GRC',
		'nav.console.home': 'Início',
		'nav.console.profile': 'Perfil',
		'nav.console.systems': 'Sistemas',
		'nav.console.workstation-protect': 'Workstation Protect',
		'nav.console.users': 'Usuários',
		'nav.extension': 'Extension',
		'nav.workstation-protect': 'Workstation Protect',
		'nav.faq': 'Perguntas frequentes',
		'nav.faq.application': 'Aplicativo',
		'nav.faq.console': 'Console',
		'nav.faq.extension': 'Extensão',
		'nav.faq.workstation-protect': 'Workstation Protect',
		'nav.troubleshooting': 'Resolução de problemas',
		'nav.troubleshooting.application': 'Aplicativo',
		'nav.troubleshooting.console': 'Console',
		'nav.troubleshooting.extension': 'Extensão',
		'nav.troubleshooting.workstation-protect': 'Workstation Protect',
		'nav.installation': 'Instalação',
		'nav.installation.install-application': 'Instalar aplicativo',
		'nav.installation.install-extension': 'Instalar extensão',
		'nav.user-guide': 'Guia de usuario',
		'nav.video': 'Vídeos explicativos',
	},
	es: {
		'search.dialog-label': 'Buscar en la documentación',
		'search.no-results': 'No hay resultados para “{{query}}”.',
		'search.placeholder': 'Buscar en la documentación',
		'search.results-count': '{{count}} resultados',
		'search.trigger': 'Buscar...',
		'doc.breadcrumb': 'Ruta de navegación',
		'doc.copied': 'Copiado',
		'note.note': 'Nota:',
		'note.tip': 'Consejo:',
		'note.important': 'Importante:',
		'note.warning': 'Advertencia:',
		'doc.copy-code': 'Copiar código',
		'doc.copy-page': 'Copiar página',
		'doc.on-this-page': 'En esta página',
		'doc.next': 'Siguiente',
		'doc.previous': 'Anterior',
		'footer.copyright': 'Copyright {{year}} MyCena Limited. Todos los derechos reservados.',
		'footer.disclaimer': 'Aviso Legal',
		'footer.privacy': 'Política de Privacidad',
		'footer.terms': 'Términos y Condiciones',
		'lang.de': 'Deutsch',
		'lang.en': 'English',
		'lang.es': 'Español',
		'lang.fr': 'Français',
		'lang.it': 'Italiano',
		'lang.pt': 'Português',
		'lang.zh-TW': '繁體中文',
		'lightbox.close': 'Cerrar',
		'lightbox.label': 'Visor de capturas de pantalla',
		'lightbox.next': 'Captura de pantalla siguiente',
		'lightbox.previous': 'Captura de pantalla anterior',
		'nav.application': 'aplicación',
		'nav.application.credentials': 'Credenciales',
		'nav.application.credentials.access-kit': 'Kit de acceso',
		'nav.application.credentials.credentials-list': 'Lista de credenciales',
		'nav.application.credentials.credit-cards': 'Tarjetas de crédito',
		'nav.application.credentials.levels': 'Niveles',
		'nav.application.settings': 'Configuración',
		'nav.application.settings.access-settings': 'Acceso a la configuración',
		'nav.application.settings.account-settings': 'Configuración de la cuenta',
		'nav.application.settings.security-settings': 'Configuración de seguridad',
		'nav.application.side-menu': 'Menú lateral',
		'nav.authentication': 'Autenticación',
		'nav.authentication.connect-application': 'Cómo conectarse a la aplicación',
		'nav.authentication.connect-console': 'Cómo conectarse a la consola',
		'nav.authentication.connect-extension': 'Cómo conectarse a la extensión',
		'nav.console': 'Consola',
		'nav.console.access-kit': 'Kit de Acceso',
		'nav.console.assistant': 'Asistente IA',
		'nav.console.credit-cards': 'Tarjetas de crédito',
		'nav.console.grc': 'GRC',
		'nav.console.home': 'Inicio',
		'nav.console.profile': 'Perfil',
		'nav.console.systems': 'Sistemas',
		'nav.console.workstation-protect': 'Workstation Protect',
		'nav.console.users': 'Usuarios',
		'nav.extension': 'Extensión',
		'nav.workstation-protect': 'Workstation Protect',
		'nav.faq': 'Preguntas frecuentes',
		'nav.faq.application': 'Aplicación',
		'nav.faq.console': 'Consola',
		'nav.faq.extension': 'Extensión',
		'nav.faq.workstation-protect': 'Workstation Protect',
		'nav.troubleshooting': 'Resolución de problemas',
		'nav.troubleshooting.application': 'Aplicación',
		'nav.troubleshooting.console': 'Consola',
		'nav.troubleshooting.extension': 'Extensión',
		'nav.troubleshooting.workstation-protect': 'Workstation Protect',
		'nav.installation': 'Instalación',
		'nav.installation.install-application': 'Instalar aplicación',
		'nav.installation.install-extension': 'Instalar extensión',
		'nav.user-guide': 'Guía del usuario',
		'nav.video': 'Vídeos instructivos',
	},
	fr: {
		'search.dialog-label': 'Rechercher dans la documentation',
		'search.no-results': 'Aucun résultat pour « {{query}} ».',
		'search.placeholder': 'Rechercher dans la documentation',
		'search.results-count': '{{count}} résultats',
		'search.trigger': 'Rechercher...',
		'doc.breadcrumb': 'Fil d’Ariane',
		'doc.copied': 'Copié',
		'note.note': 'Remarque :',
		'note.tip': 'Astuce :',
		'note.important': 'Important :',
		'note.warning': 'Avertissement :',
		'doc.copy-code': 'Copier le code',
		'doc.copy-page': 'Copier la page',
		'doc.on-this-page': 'Sur cette page',
		'doc.next': 'Suivant',
		'doc.previous': 'Précédent',
		'footer.copyright': 'Copyright {{year}} MyCena Limited. Tous droits réservés.',
		'footer.disclaimer': 'Mentions légales',
		'footer.privacy': 'Politique de confidentialité',
		'footer.terms': 'Conditions générales',
		'lang.de': 'Deutsch',
		'lang.en': 'English',
		'lang.es': 'Español',
		'lang.fr': 'Français',
		'lang.it': 'Italiano',
		'lang.pt': 'Português',
		'lang.zh-TW': '繁體中文',
		'lightbox.close': 'Fermer',
		'lightbox.label': 'Visionneuse de captures d’écran',
		'lightbox.next': 'Capture d’écran suivante',
		'lightbox.previous': 'Capture d’écran précédente',
		'nav.application': 'Application',
		'nav.application.credentials': 'Identifiants',
		'nav.application.credentials.access-kit': 'Kit d’accès',
		'nav.application.credentials.credentials-list': 'Liste des identifiants',
		'nav.application.credentials.credit-cards': 'Cartes de crédit',
		'nav.application.credentials.levels': 'Niveaux',
		'nav.application.settings': 'Paramètres',
		'nav.application.settings.access-settings': 'Accès aux paramètres',
		'nav.application.settings.account-settings': 'Paramètres du compte',
		'nav.application.settings.security-settings': 'Paramètres de sécurité',
		'nav.application.side-menu': 'Menu latéral',
		'nav.authentication': 'Authentification',
		'nav.authentication.connect-application': 'Comment se connecter à l’application',
		'nav.authentication.connect-console': 'Comment se connecter à la console',
		'nav.authentication.connect-extension': 'Comment se connecter à l’extension',
		'nav.console': 'Console',
		'nav.console.access-kit': 'Kit d’accès',
		'nav.console.assistant': 'Assistant IA',
		'nav.console.credit-cards': 'Cartes de crédit',
		'nav.console.grc': 'GRC',
		'nav.console.home': 'Accueil',
		'nav.console.profile': 'Profil',
		'nav.console.systems': 'Systèmes',
		'nav.console.workstation-protect': 'Workstation Protect',
		'nav.console.users': 'Utilisateurs',
		'nav.extension': 'Extension',
		'nav.workstation-protect': 'Workstation Protect',
		'nav.faq': 'Questions fréquentes',
		'nav.faq.application': 'Application',
		'nav.faq.console': 'Console',
		'nav.faq.extension': 'Extension',
		'nav.faq.workstation-protect': 'Workstation Protect',
		'nav.troubleshooting': 'Dépannage',
		'nav.troubleshooting.application': 'Application',
		'nav.troubleshooting.console': 'Console',
		'nav.troubleshooting.extension': 'Extension',
		'nav.troubleshooting.workstation-protect': 'Workstation Protect',
		'nav.installation': 'Installation',
		'nav.installation.install-application': 'Installer l’application',
		'nav.installation.install-extension': 'Installer l’extension',
		'nav.user-guide': 'Guide de l’utilisateur',
		'nav.video': 'Vidéos explicatives',
	},
	de: {
		'search.dialog-label': 'Dokumentation durchsuchen',
		'search.no-results': 'Keine Ergebnisse für „{{query}}“.',
		'search.placeholder': 'Dokumentation durchsuchen',
		'search.results-count': '{{count}} Ergebnisse',
		'search.trigger': 'Suchen...',
		'doc.breadcrumb': 'Navigationspfad',
		'doc.copied': 'Kopiert',
		'note.note': 'Hinweis:',
		'note.tip': 'Tipp:',
		'note.important': 'Wichtig:',
		'note.warning': 'Warnung:',
		'doc.copy-code': 'Code kopieren',
		'doc.copy-page': 'Seite kopieren',
		'doc.on-this-page': 'Auf dieser Seite',
		'doc.next': 'Weiter',
		'doc.previous': 'Zurück',
		'footer.copyright': 'Copyright {{year}} MyCena Limited. Alle Rechte vorbehalten.',
		'footer.disclaimer': 'Haftungsausschluss',
		'footer.privacy': 'Datenschutzrichtlinie',
		'footer.terms': 'Allgemeine Geschäftsbedingungen',
		'lang.de': 'Deutsch',
		'lang.en': 'English',
		'lang.es': 'Español',
		'lang.fr': 'Français',
		'lang.it': 'Italiano',
		'lang.pt': 'Português',
		'lang.zh-TW': '繁體中文',
		'lightbox.close': 'Schließen',
		'lightbox.label': 'Screenshot-Betrachter',
		'lightbox.next': 'Nächster Screenshot',
		'lightbox.previous': 'Vorheriger Screenshot',
		'nav.application': 'Anwendung',
		'nav.application.credentials': 'Anmeldedaten',
		'nav.application.credentials.access-kit': 'Zugangskit',
		'nav.application.credentials.credentials-list': 'Anmeldedatenliste',
		'nav.application.credentials.credit-cards': 'Kreditkarten',
		'nav.application.credentials.levels': 'Stufen',
		'nav.application.settings': 'Einstellungen',
		'nav.application.settings.access-settings': 'Zugriff auf Einstellungen',
		'nav.application.settings.account-settings': 'Kontoeinstellungen',
		'nav.application.settings.security-settings': 'Sicherheitseinstellungen',
		'nav.application.side-menu': 'Seitenmenü',
		'nav.authentication': 'Authentifizierung',
		'nav.authentication.connect-application': 'Mit der Anwendung verbinden',
		'nav.authentication.connect-console': 'Mit der Konsole verbinden',
		'nav.authentication.connect-extension': 'Mit der Erweiterung verbinden',
		'nav.console': 'Konsole',
		'nav.console.access-kit': 'Zugangskit',
		'nav.console.assistant': 'KI-Assistent',
		'nav.console.credit-cards': 'Kreditkarten',
		'nav.console.grc': 'GRC',
		'nav.console.home': 'Startseite',
		'nav.console.profile': 'Profil',
		'nav.console.systems': 'Systeme',
		'nav.console.workstation-protect': 'Workstation Protect',
		'nav.console.users': 'Benutzer',
		'nav.extension': 'Erweiterung',
		'nav.workstation-protect': 'Workstation Protect',
		'nav.faq': 'Häufig gestellte Fragen',
		'nav.faq.application': 'Anwendung',
		'nav.faq.console': 'Konsole',
		'nav.faq.extension': 'Erweiterung',
		'nav.faq.workstation-protect': 'Workstation Protect',
		'nav.troubleshooting': 'Fehlerbehebung',
		'nav.troubleshooting.application': 'Anwendung',
		'nav.troubleshooting.console': 'Konsole',
		'nav.troubleshooting.extension': 'Erweiterung',
		'nav.troubleshooting.workstation-protect': 'Workstation Protect',
		'nav.installation': 'Installation',
		'nav.installation.install-application': 'Anwendung installieren',
		'nav.installation.install-extension': 'Erweiterung installieren',
		'nav.user-guide': 'Benutzerhandbuch',
		'nav.video': 'Anleitungsvideos',
	},
	it: {
		'search.dialog-label': 'Cerca nella documentazione',
		'search.no-results': 'Nessun risultato per “{{query}}”.',
		'search.placeholder': 'Cerca nella documentazione',
		'search.results-count': '{{count}} risultati',
		'search.trigger': 'Cerca...',
		'doc.breadcrumb': 'Percorso di navigazione',
		'doc.copied': 'Copiato',
		'note.note': 'Nota:',
		'note.tip': 'Suggerimento:',
		'note.important': 'Importante:',
		'note.warning': 'Avviso:',
		'doc.copy-code': 'Copia codice',
		'doc.copy-page': 'Copia pagina',
		'doc.on-this-page': 'In questa pagina',
		'doc.next': 'Avanti',
		'doc.previous': 'Precedente',
		'footer.copyright': 'Copyright {{year}} MyCena Limited. Tutti i diritti riservati.',
		'footer.disclaimer': 'Note legali',
		'footer.privacy': 'Informativa sulla privacy',
		'footer.terms': 'Termini e condizioni',
		'lang.de': 'Deutsch',
		'lang.en': 'English',
		'lang.es': 'Español',
		'lang.fr': 'Français',
		'lang.it': 'Italiano',
		'lang.pt': 'Português',
		'lang.zh-TW': '繁體中文',
		'lightbox.close': 'Chiudi',
		'lightbox.label': 'Visualizzatore di schermate',
		'lightbox.next': 'Schermata successiva',
		'lightbox.previous': 'Schermata precedente',
		'nav.application': 'Applicazione',
		'nav.application.credentials': 'Credenziali',
		'nav.application.credentials.access-kit': 'Kit di accesso',
		'nav.application.credentials.credentials-list': 'Elenco delle credenziali',
		'nav.application.credentials.credit-cards': 'Carte di credito',
		'nav.application.credentials.levels': 'Livelli',
		'nav.application.settings': 'Impostazioni',
		'nav.application.settings.access-settings': 'Accesso alle impostazioni',
		'nav.application.settings.account-settings': 'Impostazioni account',
		'nav.application.settings.security-settings': 'Impostazioni di sicurezza',
		'nav.application.side-menu': 'Menu laterale',
		'nav.authentication': 'Autenticazione',
		'nav.authentication.connect-application': 'Come connettersi all’applicazione',
		'nav.authentication.connect-console': 'Come connettersi alla console',
		'nav.authentication.connect-extension': 'Come connettersi all’estensione',
		'nav.console': 'Console',
		'nav.console.access-kit': 'Kit di accesso',
		'nav.console.assistant': 'Assistente IA',
		'nav.console.credit-cards': 'Carte di credito',
		'nav.console.grc': 'GRC',
		'nav.console.home': 'Home',
		'nav.console.profile': 'Profilo',
		'nav.console.systems': 'Sistemi',
		'nav.console.workstation-protect': 'Workstation Protect',
		'nav.console.users': 'Utenti',
		'nav.extension': 'Estensione',
		'nav.workstation-protect': 'Workstation Protect',
		'nav.faq': 'Domande frequenti',
		'nav.faq.application': 'Applicazione',
		'nav.faq.console': 'Console',
		'nav.faq.extension': 'Estensione',
		'nav.faq.workstation-protect': 'Workstation Protect',
		'nav.troubleshooting': 'Risoluzione dei problemi',
		'nav.troubleshooting.application': 'Applicazione',
		'nav.troubleshooting.console': 'Console',
		'nav.troubleshooting.extension': 'Estensione',
		'nav.troubleshooting.workstation-protect': 'Workstation Protect',
		'nav.installation': 'Installazione',
		'nav.installation.install-application': 'Installare l’applicazione',
		'nav.installation.install-extension': 'Installare l’estensione',
		'nav.user-guide': 'Guida dell’utente',
		'nav.video': 'Video tutorial',
	},
	'zh-TW': {
		'search.dialog-label': '搜尋說明文件',
		'search.no-results': '找不到「{{query}}」的搜尋結果。',
		'search.placeholder': '搜尋說明文件',
		'search.results-count': '{{count}} 筆結果',
		'search.trigger': '搜尋...',
		'doc.breadcrumb': '導覽路徑',
		'doc.copied': '已複製',
		'note.note': '附註：',
		'note.tip': '提示：',
		'note.important': '重要：',
		'note.warning': '警告：',
		'doc.copy-code': '複製程式碼',
		'doc.copy-page': '複製頁面',
		'doc.on-this-page': '本頁內容',
		'doc.next': '下一頁',
		'doc.previous': '上一頁',
		'footer.copyright': 'Copyright {{year}} MyCena Limited. 保留所有權利。',
		'footer.disclaimer': '免責聲明',
		'footer.privacy': '隱私權政策',
		'footer.terms': '條款與細則',
		'lang.de': 'Deutsch',
		'lang.en': 'English',
		'lang.es': 'Español',
		'lang.fr': 'Français',
		'lang.it': 'Italiano',
		'lang.pt': 'Português',
		'lang.zh-TW': '繁體中文',
		'lightbox.close': '關閉',
		'lightbox.label': '螢幕截圖檢視器',
		'lightbox.next': '下一張截圖',
		'lightbox.previous': '上一張截圖',
		'nav.application': '應用程式',
		'nav.application.credentials': '憑證',
		'nav.application.credentials.access-kit': '存取套件',
		'nav.application.credentials.credentials-list': '憑證清單',
		'nav.application.credentials.credit-cards': '信用卡',
		'nav.application.credentials.levels': '等級',
		'nav.application.settings': '設定',
		'nav.application.settings.access-settings': '存取設定',
		'nav.application.settings.account-settings': '帳號設定',
		'nav.application.settings.security-settings': '安全設定',
		'nav.application.side-menu': '側邊選單',
		'nav.authentication': '驗證',
		'nav.authentication.connect-application': '如何連接應用程式',
		'nav.authentication.connect-console': '如何連接主控台',
		'nav.authentication.connect-extension': '如何連接擴充功能',
		'nav.console': '主控台',
		'nav.console.access-kit': '存取套件',
		'nav.console.assistant': 'AI 助理',
		'nav.console.credit-cards': '信用卡',
		'nav.console.grc': 'GRC',
		'nav.console.home': '首頁',
		'nav.console.profile': '個人資料',
		'nav.console.systems': '系統',
		'nav.console.workstation-protect': 'Workstation Protect',
		'nav.console.users': '使用者',
		'nav.extension': '擴充功能',
		'nav.workstation-protect': 'Workstation Protect',
		'nav.faq': '常見問題',
		'nav.faq.application': '應用程式',
		'nav.faq.console': '控制台',
		'nav.faq.extension': '擴充功能',
		'nav.faq.workstation-protect': 'Workstation Protect',
		'nav.troubleshooting': '疑難排解',
		'nav.troubleshooting.application': '應用程式',
		'nav.troubleshooting.console': '控制台',
		'nav.troubleshooting.extension': '擴充功能',
		'nav.troubleshooting.workstation-protect': 'Workstation Protect',
		'nav.installation': '安裝',
		'nav.installation.install-application': '安裝應用程式',
		'nav.installation.install-extension': '安裝擴充功能',
		'nav.user-guide': '使用者指南',
		'nav.video': '操作教學影片',
	},
};
